<template>
  <div class="container my-4">
    <form class="row mb-3" @submit.prevent="loadMetrics">
      <div class="col-md-3">
        <label class="form-label">From</label>
        <input type="date" v-model="filters.dateFrom" class="form-control" />
      </div>
      <div class="col-md-3">
        <label class="form-label">To</label>
        <input type="date" v-model="filters.dateTo" class="form-control" />
      </div>

      <div class="col-md-3">
        <label class="form-label">Store</label>
        <div class="w-100">
          <details class="w-100">
            <summary
              class="btn btn-outline-secondary w-100 text-start d-flex justify-content-between align-items-center"
              style="list-style:none;">
              <span>
                <span v-if="filters.locations.length === 0">All</span>
                <span v-else>
                  {{ filters.locations.slice(0, 2).join(', ') }}
                  <span v-if="filters.locations.length > 2">+{{ filters.locations.length - 2 }} more</span>
                </span>
              </span>
              <i class="bi bi-caret-down-fill"></i>
            </summary>

            <div class="dropdown-menu w-100 p-2" style="display:block;position:static;max-height:300px;overflow:auto;">
              <div class="mb-2">
                <label class="form-check">
                  <input class="form-check-input" type="checkbox" :checked="isAllSelected"
                    @change="toggleSelectAll($event)" />
                  <span class="form-check-label">All</span>
                </label>
              </div>
              <div v-for="name in uniqueLocations" :key="name" class="form-check">
                <input class="form-check-input" type="checkbox" :id="`loc-${name}`" :value="name"
                  v-model="filters.locations" />
                <label class="form-check-label" :for="`loc-${name}`">{{ name }}</label>
              </div>
            </div>
          </details>
        </div>
      </div>

      <div class="col-md-3 d-flex align-items-end gap-2">
        <button type="submit" class="btn btn-primary flex-fill" @click="loadDonations">Apply Filters</button>
        <button type="button" class="btn btn-secondary flex-fill" @click="resetFilters">Reset</button>
      </div>
    </form>

    <div class="row">
      <div class="row justify-content-center g-4">
        <div class="col-md-4" v-for="card in cards" :key="card.title">
          <div class="card text-center shadow-sm metric-card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">{{ card.title }}</h5>

              <button class="btn btn-sm btn-outline-secondary" @click="collapsed[card.title] = !collapsed[card.title]">
                <i :class="collapsed[card.title] ? 'bi bi-plus' : 'bi bi-dash'"></i>
              </button>
            </div>

            <div class="card-body" v-if="!collapsed[card.title]">
              <p class="display-6">{{ card.value }}</p>
            </div>
          </div>

        </div>
      </div>

    </div>

    <div class="mt-4 d-flex justify-content-between align-items-center">
      <h4>Donation Info Items</h4>
      <button type="button" class="btn btn-outline-primary" @click="aggregateByStore = !aggregateByStore">
        {{ aggregateByStore ? 'Show Details' : 'Aggregate by Store' }}
      </button>
    </div>

    <table class="table table-striped mt-2">
      <thead>
        <tr>
          <th v-if="!aggregateByStore">Ticket</th>
          <th>Location</th>
          <th>Date</th>
          <th>{{ cleanName('Donor_Count_DI') }}</th>
          <th>{{ cleanName('Raw_Text_DI') }}</th>
          <th>{{ cleanName('Raw_Wares_DI') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in (aggregateByStore ? aggregatedItems : reshapedItems)"
          :key="aggregateByStore ? row.location : row.unique_qr_code">
          <td v-if="!aggregateByStore">{{ row.unique_qr_code }}</td>
          <td>{{ row.location }}</td>
          <td>{{ formatSanDiegoDateTime(aggregateByStore ? row.latestDate : row.created_at) }}</td>
          <td>{{ formatCategoryName(String(row.Donor_Count_DI)) }}</td>
          <td>{{ formatCategoryName(String(row.RT_Bin_Gaylord_DI)) }}</td>
          <td>{{ formatCategoryName(String(row.RW_Bin_Gaylord_DI)) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>


<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { formatSanDiegoDateTime } from '../utils/dateTime';

const metrics = ref({
  avg_request_to_delivery: 0,
  avg_delivery_to_fulfilled: 0,
  avg_requests_per_day: 0,
  backorder_count: 0
});

const collapsed = ref({});




const filters = ref({ dateFrom: '', dateTo: '', locations: [] });
const locations = ref([]);
const categoryItems = ref([]);
const donations = ref([]);
const aggregateByStore = ref(false);

function getAuthHeaders() {
  const token = sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function loadMetrics() {
  try {
    const params = {
      dateFrom: filters.value.dateFrom,
      dateTo: filters.value.dateTo,
      locations: filters.value.locations

    };
    const { data } = await axios.get(
      `${process.env.VUE_APP_API_URL}/api/ordermetrics`,
      { params, headers: getAuthHeaders() }
    );
    metrics.value = {
      avg_request_to_delivery: Number(data.avg_request_to_delivery || 0),
      avg_delivery_to_fulfilled: Number(data.avg_delivery_to_fulfilled || 0),
      avg_requests_per_day: Number(data.avg_requests_per_day || 0),
      backorder_count: Number(data.backorder_count || 0)
    };

  } catch (err) {
    console.error('Failed to load metrics:', err);
  }
}

async function resetFilters() {
  filters.value = { dateFrom: '', dateTo: '', locations: [] };
  loadMetrics();
}

async function loadLocations() {
  const { data } = await axios.get(
    `${process.env.VUE_APP_API_URL}/api/locations`,
    { headers: getAuthHeaders() }
  );
  locations.value = data;
}

async function loadDonations() {
  const { data } = await axios.get(
    `${process.env.VUE_APP_API_URL}/api/donations`,
    { params: filters.value, headers: getAuthHeaders() }
  );
  donations.value = data;
}

const uniqueLocations = computed(() => {
  return Array.from(new Set(locations.value.map(l => l.store_name)));
});

const isAllSelected = computed(() => {
  const all = uniqueLocations.value || [];
  return filters.value.locations.length === 0 || filters.value.locations.length === all.length;
});

function toggleSelectAll(e) {
  if (e.target.checked) {
    filters.value.locations = uniqueLocations.value.slice();
  } else {
    filters.value.locations = [];
  }
}

onMounted(() => {
  loadMetrics();
  loadLocations();
  loadDonationInfo();
  loadDonations();
});

function fmtMinutes(val) {
  const n = Number(val);
  if (!isFinite(n) || n < 0) return '—';
  const totalSeconds = Math.round(n * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${hours}hr ${mins}min ${secs}sec`;
}

const loadDonationInfo = async () => {
  try {
    const { data } = await axios.get(
      `${process.env.VUE_APP_API_URL}/api/donations-info`,
      { params: filters.value, headers: getAuthHeaders() }
    );
    categoryItems.value = data;
  } catch (err) {
    console.error('Failed to load Donation_Info items:', err);
  }
};

function formatCategoryName(cat) {
  return cat.replace(/_/g, ' ');
}

function cleanName(name) {
  if (!name) return '';

  let cleaned = name.replace(/^#_of_/, '');

  const parts = cleaned.split('_');

  parts.pop();

  return parts.join(' ');
}

const filteredItems = computed(() => {
  return categoryItems.value.filter(item => {
    const itemDate = new Date(item.created_at);
    const from = filters.value.dateFrom ? new Date(filters.value.dateFrom) : null;
    const to = filters.value.dateTo ? new Date(filters.value.dateTo) : null;
    if (from && itemDate < from) return false;
    if (to && itemDate > to) return false;

    if (filters.value.locations.length > 0 &&
      !filters.value.locations.includes(item.location)) {
      return false;
    }
    return true;
  });
});


const reshapedItems = computed(() => {
  const grouped = {};
  for (const item of filteredItems.value) {

    const key = item.unique_qr_code;
    if (!grouped[key]) {
      grouped[key] = {
        unique_qr_code: item.unique_qr_code,
        location: item.location,
        created_at: item.created_at,
        Donor_Count_DI: 0,
        RT_Bin_Gaylord_DI: 0,
        RW_Bin_Gaylord_DI: 0
      };
    }
    if (item.category_name === 'Donor_Count_DI') grouped[key].Donor_Count_DI = item.quantity;
    if (item.category_name === '#_of_RT_DI') grouped[key].RT_Bin_Gaylord_DI = item.quantity;
    if (item.category_name === '#_of_RW_DI') grouped[key].RW_Bin_Gaylord_DI = item.quantity;
  }
  return Object.values(grouped);
});

const aggregatedItems = computed(() => {
  const grouped = {};
  for (const item of reshapedItems.value) {
    const key = item.location;
    if (!grouped[key]) {
      grouped[key] = {
        location: item.location,
        Donor_Count_DI: 0,
        RT_Bin_Gaylord_DI: 0,
        RW_Bin_Gaylord_DI: 0,
        latestDate: null
      };
    }
    grouped[key].Donor_Count_DI += Number(item.Donor_Count_DI) || 0;
    grouped[key].RT_Bin_Gaylord_DI += Number(item.RT_Bin_Gaylord_DI) || 0;
    grouped[key].RW_Bin_Gaylord_DI += Number(item.RW_Bin_Gaylord_DI) || 0;
    const d = new Date(item.created_at);
    if (!grouped[key].latestDate || d > new Date(grouped[key].latestDate)) {
      grouped[key].latestDate = item.created_at;
    }
  }
  return Object.values(grouped);
});

function fmtNumber(val) {
  const n = Number(val);
  if (!isFinite(n) || n <= 0) return '—';
  return n.toFixed(2);
}

const cards = computed(() => [
  { title: 'Avg Request → Delivery', value: fmtMinutes(metrics.value.avg_request_to_delivery) },
  { title: 'Avg Delivery → Fulfilled', value: fmtMinutes(metrics.value.avg_delivery_to_fulfilled) },
  { title: 'Avg Requests / Day', value: fmtNumber(metrics.value.avg_requests_per_day) },
  { title: 'Backorders', value: fmtNumber(metrics.value.backorder_count) }
]);

watch(cards, () => {
  cards.value.forEach(card => {
    if (!(card.title in collapsed.value)) {
      collapsed.value[card.title] = false;
    }
  });
});

</script>
